import React, { Dispatch, SetStateAction, KeyboardEvent, useState } from 'react'
import { MagnifyingGlass, CaretDown, X } from '@phosphor-icons/react'
import { Select, SelectTrigger, SelectContent, SelectItem } from '@shopflo/ui'

export interface SearchInputParam {
  value: string
  label: string
  placeholder?: string
}

interface SearchInputProps {
  searchText: string
  onChange: Dispatch<SetStateAction<string>>
  onSubmit?: () => void
  selectedParam?: string
  onParamChange?: (value: string) => void
  params?: SearchInputParam[]
  className?: string
  onClose?: () => void
}

const SearchInput: React.FC<SearchInputProps> = ({
  searchText,
  onChange,
  onSubmit,
  selectedParam,
  onParamChange,
  params,
  className = '',
  onClose
}) => {
  const [focused, setFocused] = useState(false)
  const currentParam = params?.find(p => p.value === selectedParam)
  const placeholder = currentParam?.placeholder ?? 'Search...'

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onSubmit?.()
  }

  const handleChange = (value: string) => {
    if (selectedParam === 'phone') {
      // Allow + only at start, digits only, max 13 chars (+[country code][10 digits])
      const filtered = value.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '')
      if (filtered.length > 13) return
      onChange(filtered)
    } else if (selectedParam === 'email') {
      // Block strings that are purely numeric (no @ or letters)
      const isPlainNumber = /^\d+$/.test(value)
      if (!isPlainNumber) onChange(value)
    } else {
      onChange(value)
    }
  }

  return (
    <div
      className={`flex items-center gap-2 w-full border bg-white transition-colors ${focused ? 'border-zinc-400' : 'border-zinc-200'} ${className}`}
      style={{
        height: 28,
        padding: '0 8px 0 4px',
        borderRadius: 8,
      }}
    >
      <MagnifyingGlass size={14} className='text-zinc-400 flex-shrink-0' />

      {params && params.length > 0 && onParamChange ? (
        <>
          <Select value={selectedParam ?? ''} onValueChange={onParamChange}>
            <SelectTrigger
              className='h-fit w-fit border-none bg-transparent p-0 shadow-none focus:ring-0 gap-0.5'
              showCaret={false}
            >
              <span
                className='text-text-xs-medium whitespace-nowrap'
                style={{ color: '#52525C', letterSpacing: '-0.12px' }}
              >
                {currentParam?.label ?? 'Search'}
              </span>
              <div
                className='flex items-center justify-center flex-shrink-0'
                style={{ width: 16, height: 16, borderRadius: 6.4 }}
              >
                <CaretDown size={10} color='#71717A' weight='bold' />
              </div>
            </SelectTrigger>
            <SelectContent align='start'>
              {params.map(p => (
                <SelectItem key={p.value} value={p.value} className='text-xs'>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className='w-px h-3.5 bg-zinc-200 flex-shrink-0' />
        </>
      ) : null}

      <input
        type='text'
        value={searchText}
        onChange={e => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        autoFocus
        className='flex-1 bg-transparent text-text-xs-medium text-zinc-800 placeholder:text-zinc-400 outline-none border-none min-w-0'
        style={{ letterSpacing: '-0.12px' }}
      />
      {onClose && (
        <button
          type='button'
          onClick={onClose}
          className='flex items-center justify-center flex-shrink-0 text-zinc-400 hover:text-zinc-600 transition-colors'
        >
          <X size={12} weight='bold' />
        </button>
      )}
    </div>
  )
}

export default SearchInput
